"use client";

import {
  createContext,
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { useReducedMotion } from "motion/react";
import type { LucideIcon } from "lucide-react";
import DynamicIcon from "@/components/ui/dynamic-icon";
import {
  ANIMATED_ICON_LOADERS,
  resolveAnimatedIconName,
  type AnimatedIconComponent,
  type AnimatedIconHandle,
} from "@/components/animated-icons/registry";
import {
  ANIMATED_ICON_MAX_RUN_MS,
  ANIMATED_ICON_REPEAT_DELAY_MS,
} from "@/config/timing";
import { cn } from "@/lib/utils";

const AnimatedIconsEnabledContext = createContext(true);

export function AnimatedIconsProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  return (
    <AnimatedIconsEnabledContext.Provider value={enabled}>
      {children}
    </AnimatedIconsEnabledContext.Provider>
  );
}

const loadedIcons = new Map<string, AnimatedIconComponent>();
const pendingIcons = new Map<string, Promise<AnimatedIconComponent>>();

function loadAnimatedIcon(name: string): Promise<AnimatedIconComponent> {
  const pending = pendingIcons.get(name);
  if (pending) return pending;
  const promise = ANIMATED_ICON_LOADERS[name]().then(
    (component) => {
      loadedIcons.set(name, component);
      return component;
    },
    (error: unknown) => {
      pendingIcons.delete(name);
      throw error;
    },
  );
  pendingIcons.set(name, promise);
  return promise;
}

function useAnimatedIconComponent(
  name: string | null,
): AnimatedIconComponent | null {
  const [entry, setEntry] = useState<{
    name: string;
    component: AnimatedIconComponent;
  } | null>(null);

  useEffect(() => {
    if (!name) return;
    const cached = loadedIcons.get(name);
    if (cached) {
      setEntry({ name, component: cached });
      return;
    }
    let alive = true;
    loadAnimatedIcon(name).then(
      (component) => {
        if (alive) setEntry({ name, component });
      },
      () => {},
    );
    return () => {
      alive = false;
    };
  }, [name]);

  return entry && entry.name === name ? entry.component : null;
}

type VisibilityListener = (visible: boolean) => void;

const visibilityListeners = new Map<Element, VisibilityListener>();
let sharedObserver: IntersectionObserver | null = null;

function observeVisibility(
  element: Element,
  listener: VisibilityListener,
): () => void {
  if (typeof IntersectionObserver === "undefined") {
    listener(true);
    return () => {};
  }
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        visibilityListeners.get(entry.target)?.(entry.isIntersecting);
      }
    });
  }
  visibilityListeners.set(element, listener);
  sharedObserver.observe(element);
  return () => {
    visibilityListeners.delete(element);
    sharedObserver?.unobserve(element);
  };
}

function useInViewport(element: Element | null, enabled: boolean): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!element || !enabled) {
      setVisible(false);
      return;
    }
    return observeVisibility(element, setVisible);
  }, [element, enabled]);

  return visible;
}

function safely<T>(run: () => T): T | undefined {
  try {
    return run();
  } catch {
    return undefined;
  }
}

function useAnimationLoop(
  handleRef: RefObject<AnimatedIconHandle | null>,
  running: boolean,
) {
  useEffect(() => {
    if (!running) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
      });

    const loop = async () => {
      while (!cancelled) {
        const finished = safely(() => handleRef.current?.startAnimation());
        await Promise.race([
          Promise.resolve(finished).catch(() => undefined),
          wait(ANIMATED_ICON_MAX_RUN_MS),
        ]);
        clearTimeout(timer);
        if (cancelled) return;
        safely(() => handleRef.current?.stopAnimation());
        await wait(ANIMATED_ICON_REPEAT_DELAY_MS);
      }
    };
    void loop();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      safely(() => handleRef.current?.stopAnimation());
    };
  }, [handleRef, running]);
}

type AnimatedIconProps = {
  name: string;
  className?: string;
  size?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  fallback?: LucideIcon;
};

const INHERIT_STROKE = "size-full [stroke-width:inherit]";

export const AnimatedIcon = memo(function AnimatedIcon({
  name,
  className,
  size,
  strokeWidth,
  style,
  fallback: Fallback,
}: AnimatedIconProps) {
  const enabledByContext = useContext(AnimatedIconsEnabledContext);
  const reducedMotion = useReducedMotion();
  const animatedName = resolveAnimatedIconName(name);
  const active = enabledByContext && !reducedMotion && animatedName !== null;

  const [element, setElement] = useState<HTMLSpanElement | null>(null);
  const visible = useInViewport(element, active);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (active && visible) setRequested(true);
  }, [active, visible]);

  const Component = useAnimatedIconComponent(
    active && requested ? animatedName : null,
  );
  const handleRef = useRef<AnimatedIconHandle | null>(null);
  const showAnimated = active && Component !== null;

  useAnimationLoop(handleRef, showAnimated && visible);

  const wrapperStyle: CSSProperties = {
    ...(size === undefined ? null : { width: size, height: size }),
    ...(strokeWidth === undefined ? null : { strokeWidth }),
    ...style,
  };

  return (
    <span
      ref={setElement}
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center stroke-2",
        className,
      )}
      style={wrapperStyle}
    >
      {showAnimated && Component ? (
        <Component
          ref={handleRef}
          className="size-full"
          style={{ strokeWidth: "inherit" }}
        />
      ) : Fallback ? (
        <Fallback className={INHERIT_STROKE} />
      ) : (
        <DynamicIcon
          name={name as Parameters<typeof DynamicIcon>[0]["name"]}
          className={INHERIT_STROKE}
        />
      )}
    </span>
  );
});
